/**
 * OnboardingWizardPage - multi-step organisation signup wizard.
 *
 * Step 1: Company basics (name, slug, admin email/password, first/last name)
 * Step 2: Host type selection (Vehicle, Property, Vessel, or Standalone)
 * Step 3: Confirmation + account creation via POST /api/organisations/signup/
 *
 * The step system is extensible: steps are defined as an array of
 * { label, component } objects so future steps (e.g. AI-powered) can be added.
 */

import { useState, useCallback, useMemo } from 'react'
import { useNavigate } from 'react-router'
import { toast } from 'sonner'
import { toastApiError } from '@/shared/lib/api-error'
import { Loader2, Car, Home, Ship, Package, Check, ArrowRight, ArrowLeft, ExternalLink } from 'lucide-react'
import api from '@/admin/lib/api'
import { Logo } from '@/admin/components/ui/logo'
import { Label } from '@/shared/components/ui/label'
import { Input } from '@/shared/components/ui/input'
import { Button } from '@/shared/components/ui/button'
import { cn } from '@/shared/lib/utils'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface OnboardingState {
  // Step 1: Company basics
  companyName: string
  companySlug: string
  adminEmail: string
  adminPassword: string
  firstName: string
  lastName: string
  // Step 2: Host kinds
  hostKinds: string[]
  // Submission state
  isSubmitting: boolean
  isComplete: boolean
}

interface StepProps {
  state: OnboardingState
  update: (patch: Partial<OnboardingState>) => void
  next: () => void
  back: () => void
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function slugify(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
}

function ClaraInput({
  label,
  id,
  error,
  ...props
}: React.InputHTMLAttributes<HTMLInputElement> & { label: string; error?: string }) {
  return (
    <div>
      <Label htmlFor={id} className="clara-label block mb-2">
        {label}
      </Label>
      <Input
        id={id}
        className="w-full rounded-[2px] border-[0.5px] border-[var(--rule-strong)] bg-[var(--bg)] px-3.5 py-2.5 text-[13px] text-[var(--ink)] transition-all duration-150 focus-visible:border-[var(--green)]"
        {...props}
      />
      {error && (
        <p className="mt-1 text-[11px] text-[var(--status-error)]">
          {error}
        </p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Step 1: Company Basics
// ---------------------------------------------------------------------------

function StepCompanyBasics({ state, update, next }: StepProps) {
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [slugEdited, setSlugEdited] = useState(false)

  function handleNameChange(name: string) {
    update({ companyName: name })
    if (!slugEdited) {
      update({ companySlug: slugify(name) })
    }
  }

  function validate(): boolean {
    const e: Record<string, string> = {}
    if (!state.companyName.trim()) e.companyName = 'Company name is required'
    if (!state.companySlug.trim()) e.companySlug = 'Slug is required'
    if (!/^[a-z0-9-]+$/.test(state.companySlug)) e.companySlug = 'Slug must be lowercase letters, numbers, and hyphens'
    if (!state.adminEmail.trim()) e.adminEmail = 'Email is required'
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(state.adminEmail)) e.adminEmail = 'Enter a valid email'
    if (!state.adminPassword) e.adminPassword = 'Password is required'
    if (state.adminPassword.length < 8) e.adminPassword = 'Password must be at least 8 characters'
    setErrors(e)
    return Object.keys(e).length === 0
  }

  function handleNext() {
    if (validate()) next()
  }

  return (
    <div className="space-y-5">
      <ClaraInput
        label="Company Name"
        id="companyName"
        placeholder="Acme Warranties Ltd"
        value={state.companyName}
        onChange={(e) => handleNameChange(e.target.value)}
        error={errors.companyName}
        autoFocus
      />
      <ClaraInput
        label="Company Slug"
        id="companySlug"
        placeholder="acme-warranties"
        value={state.companySlug}
        onChange={(e) => {
          setSlugEdited(true)
          update({ companySlug: e.target.value })
        }}
        error={errors.companySlug}
      />
      <p className="-mt-3 text-[11px] text-[var(--ink-3)]">
        This will be your unique URL identifier
      </p>
      <div className="grid grid-cols-2 gap-4">
        <ClaraInput
          label="First Name"
          id="firstName"
          placeholder="Jane"
          value={state.firstName}
          onChange={(e) => update({ firstName: e.target.value })}
        />
        <ClaraInput
          label="Last Name"
          id="lastName"
          placeholder="Smith"
          value={state.lastName}
          onChange={(e) => update({ lastName: e.target.value })}
        />
      </div>
      <ClaraInput
        label="Admin Email"
        id="adminEmail"
        type="email"
        placeholder="jane@acme.com"
        value={state.adminEmail}
        onChange={(e) => update({ adminEmail: e.target.value })}
        error={errors.adminEmail}
        autoComplete="email"
      />
      <ClaraInput
        label="Password"
        id="adminPassword"
        type="password"
        placeholder="Min 8 characters"
        value={state.adminPassword}
        onChange={(e) => update({ adminPassword: e.target.value })}
        error={errors.adminPassword}
        autoComplete="new-password"
      />
      <div className="pt-2">
        <Button
          type="button"
          onClick={handleNext}
          className="w-full py-2.5 text-[11px] uppercase tracking-[0.1em] rounded-[4px] transition-all duration-150 cursor-pointer flex items-center justify-center gap-2 bg-[var(--green)] hover:bg-[var(--green-hover)] text-[var(--bg)]"
        >
          Continue
          <ArrowRight className="h-3.5 w-3.5" />
        </Button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Step 2: Host Type Selection
// ---------------------------------------------------------------------------

const HOST_TYPE_OPTIONS = [
  {
    kind: 'VEHICLE',
    label: 'Vehicles',
    description: 'Cars, vans, motorcycles',
    Icon: Car,
  },
  {
    kind: 'PROPERTY',
    label: 'Properties',
    description: 'Homes, buildings, real estate',
    Icon: Home,
  },
  {
    kind: 'VESSEL',
    label: 'Vessels',
    description: 'Boats, yachts, marine craft',
    Icon: Ship,
  },
] as const

function StepHostTypes({ state, update, next, back }: StepProps) {
  function toggle(kind: string) {
    const current = state.hostKinds
    if (current.includes(kind)) {
      update({ hostKinds: current.filter((k) => k !== kind) })
    } else {
      update({ hostKinds: [...current, kind] })
    }
  }

  const isStandaloneOnly = state.hostKinds.length === 0

  return (
    <div className="space-y-6">
      <div>
        <p className="text-[13px] leading-relaxed text-[var(--ink-2)]">
          Select all that apply. You can always add more later.
        </p>
      </div>
      <div className="grid grid-cols-2 gap-3">
        {HOST_TYPE_OPTIONS.map(({ kind, label, description, Icon }) => {
          const selected = state.hostKinds.includes(kind)
          return (
            <Button
              variant="ghost"
              key={kind}
              type="button"
              onClick={() => toggle(kind)}
              className={cn(
                'relative h-auto cursor-pointer rounded-[8px] p-4 text-left transition-all duration-150',
                selected
                  ? 'border-[1.5px] border-[var(--green)] bg-[var(--green-bg)]'
                  : 'border-[0.5px] border-[var(--rule-strong)] bg-[var(--bg)]'
              )}
            >
              {selected && (
                <div
                  className="absolute right-3 top-3 flex h-5 w-5 items-center justify-center rounded-full bg-[var(--green)]"
                >
                  <Check className="h-3 w-3 text-white" />
                </div>
              )}
              <Icon
                className={cn(
                  'mb-2 h-6 w-6',
                  selected ? 'text-[var(--green)]' : 'text-[var(--ink-3)]'
                )}
              />
              <div className="text-[13px] font-medium text-[var(--ink)]">
                {label}
              </div>
              <div className="mt-0.5 text-[11px] text-[var(--ink-3)]">
                {description}
              </div>
            </Button>
          );
        })}

        {/* Standalone card */}
        <Button
          variant="ghost"
          type="button"
          onClick={() => update({ hostKinds: [] })}
          className={cn(
            'relative h-auto cursor-pointer rounded-[8px] p-4 text-left transition-all duration-150',
            isStandaloneOnly
              ? 'border-[1.5px] border-[var(--green)] bg-[var(--green-bg)]'
              : 'border-[0.5px] border-[var(--rule-strong)] bg-[var(--bg)]'
          )}
        >
          {isStandaloneOnly && (
            <div
              className="absolute right-3 top-3 flex h-5 w-5 items-center justify-center rounded-full bg-[var(--green)]"
            >
              <Check className="h-3 w-3 text-white" />
            </div>
          )}
          <Package
            className={cn(
              'mb-2 h-6 w-6',
              isStandaloneOnly ? 'text-[var(--green)]' : 'text-[var(--ink-3)]'
            )}
          />
          <div className="text-[13px] font-medium text-[var(--ink)]">
            Standalone only
          </div>
          <div className="mt-0.5 text-[11px] text-[var(--ink-3)]">
            Products that don't attach to anything
          </div>
        </Button>
      </div>
      <p className="text-[11px] text-[var(--ink-3)]">
        Some products can be installed on a host or sold standalone - you can configure this per product later.
      </p>
      <div className="flex gap-3 pt-2">
        <Button
          variant="outline"
          type="button"
          onClick={back}
          className="flex flex-1 cursor-pointer items-center justify-center gap-2 rounded-[4px] border-[0.5px] border-[var(--rule-strong)] bg-transparent py-2.5 text-[11px] uppercase tracking-[0.1em] text-[var(--ink-3)] transition-all duration-150"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          Back
        </Button>
        <Button
          type="button"
          onClick={next}
          className="flex-[2] py-2.5 text-[11px] uppercase tracking-[0.1em] rounded-[4px] transition-all duration-150 cursor-pointer flex items-center justify-center gap-2 bg-[var(--green)] hover:bg-[var(--green-hover)] text-[var(--bg)]"
        >
          Continue
          <ArrowRight className="h-3.5 w-3.5" />
        </Button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Step 3: Confirmation
// ---------------------------------------------------------------------------

function StepConfirmation({ state, update, back }: StepProps) {
  const navigate = useNavigate()

  const hostKindLabels: Record<string, string> = {
    VEHICLE: 'Vehicle',
    PROPERTY: 'Property',
    VESSEL: 'Vessel',
  }

  async function handleCreateAccount() {
    update({ isSubmitting: true })
    try {
      const payload: Record<string, unknown> = {
        tenant_name: state.companyName.trim(),
        tenant_slug: state.companySlug.trim(),
        admin_email: state.adminEmail.trim(),
        admin_password: state.adminPassword,
        admin_first_name: state.firstName.trim(),
        admin_last_name: state.lastName.trim(),
      }
      if (state.hostKinds.length > 0) {
        payload.host_kinds = state.hostKinds
      }

      await api.post('/organisations/signup/', payload)

      update({ isSubmitting: false, isComplete: true })
    } catch (err: unknown) {
      update({ isSubmitting: false })
      toastApiError(err, 'Failed to create account. Please try again.')
    }
  }

  // Success view
  if (state.isComplete) {
    return (
      <div className="space-y-6 text-center">
        <div
          className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-[var(--green-bg)]"
        >
          <Check className="h-7 w-7 text-[var(--green)]" />
        </div>
        <div>
          <h2 className="font-display text-[28px] leading-[1.15] text-[var(--ink)]">
            Welcome aboard
          </h2>
          <p className="mt-2 text-[13px] text-[var(--ink-2)]">
            Your organisation <strong>{state.companyName}</strong> has been created.
            Sign in to get started.
          </p>
        </div>
        <div className="space-y-2 text-left">
          <p className="clara-label mb-3">Recommended next steps</p>
          <NextStepLink
            label="Manage Host Templates"
            href="/manage/host-templates"
            description="Configure the hosts your products attach to"
          />
          <NextStepLink
            label="Add Products"
            href="/manage/products"
            description="Create your first product offerings"
          />
          <NextStepLink
            label="Invite Team"
            href="/settings/users"
            description="Add team members to your organisation"
          />
        </div>
        <Button
          type="button"
          onClick={() => {
            toast.success('Account created! Please sign in.')
            navigate('/login')
          }}
          className="w-full py-2.5 text-[11px] uppercase tracking-[0.1em] rounded-[4px] transition-all duration-150 cursor-pointer bg-[var(--green)] hover:bg-[var(--green-hover)] text-[var(--bg)]"
        >
          Go to Login
        </Button>
      </div>
    );
  }

  // Confirmation view
  return (
    <div className="space-y-6">
      <div>
        <p className="text-[13px] leading-relaxed text-[var(--ink-2)]">
          Review your details before creating your account.
        </p>
      </div>
      <div
        className="space-y-4 rounded-[8px] border-[0.5px] border-[var(--rule)] bg-[var(--bg-subtle)] p-5"
      >
        <SummaryRow label="Company" value={state.companyName} />
        <SummaryRow label="Slug" value={state.companySlug} />
        <SummaryRow label="Admin" value={`${state.firstName} ${state.lastName}`.trim() || state.adminEmail} />
        <SummaryRow label="Email" value={state.adminEmail} />
        <SummaryRow
          label="Host Types"
          value={
            state.hostKinds.length > 0
              ? state.hostKinds.map((k) => hostKindLabels[k] || k).join(', ')
              : 'Standalone only (no hosts)'
          }
        />
      </div>
      <div
        className="rounded-[8px] border-[0.5px] border-[var(--rule)] bg-[var(--bg)] p-4"
      >
        <p className="clara-label mb-2">What will be set up</p>
        <ul className="space-y-1.5">
          {state.hostKinds.length > 0 && (
            <li className="flex items-start gap-2 text-[12px] text-[var(--ink-2)]">
              <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[var(--green)]" />
              Host templates for {state.hostKinds.map((k) => hostKindLabels[k] || k).join(', ')}
            </li>
          )}
          <li className="flex items-start gap-2 text-[12px] text-[var(--ink-2)]">
            <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[var(--green)]" />
            Starter product templates for each host type
          </li>
          <li className="flex items-start gap-2 text-[12px] text-[var(--ink-2)]">
            <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[var(--green)]" />
            Admin account with full access
          </li>
        </ul>
      </div>
      <div className="flex gap-3 pt-2">
        <Button
          variant="outline"
          type="button"
          onClick={back}
          disabled={state.isSubmitting}
          className="flex flex-1 cursor-pointer items-center justify-center gap-2 rounded-[4px] border-[0.5px] border-[var(--rule-strong)] bg-transparent py-2.5 text-[11px] uppercase tracking-[0.1em] text-[var(--ink-3)] transition-all duration-150 disabled:opacity-50"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          Back
        </Button>
        <Button
          type="button"
          onClick={handleCreateAccount}
          disabled={state.isSubmitting}
          className="flex-[2] py-2.5 text-[11px] uppercase tracking-[0.1em] rounded-[4px] transition-all duration-150 cursor-pointer flex items-center justify-center gap-2 disabled:opacity-50 bg-[var(--green)] hover:bg-[var(--green-hover)] text-[var(--bg)]"
        >
          {state.isSubmitting ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <>Create Account</>
          )}
        </Button>
      </div>
    </div>
  );
}

function SummaryRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline justify-between">
      <span className="text-[11px] uppercase tracking-[0.1em] text-[var(--ink-3)]">
        {label}
      </span>
      <span className="text-[13px] text-[var(--ink)]">
        {value}
      </span>
    </div>
  );
}

function NextStepLink({ label, href, description }: { label: string; href: string; description: string }) {
  return (
    <a
      href={href}
      className="flex items-center justify-between p-3 rounded-[8px] transition-all duration-150 group border-[0.5px] border-solid border-[var(--rule)] hover:border-[var(--green)]"
    >
      <div>
        <div className="text-[13px] font-medium text-[var(--ink)]">
          {label}
        </div>
        <div className="text-[11px] text-[var(--ink-3)]">
          {description}
        </div>
      </div>
      <ExternalLink className="h-3.5 w-3.5 shrink-0 text-[var(--ink-3)]" />
    </a>
  );
}

// ---------------------------------------------------------------------------
// Step definitions (extensible array)
// ---------------------------------------------------------------------------

interface StepDefinition {
  label: string
  component: React.ComponentType<StepProps>
}

const STEPS: StepDefinition[] = [
  { label: 'Company', component: StepCompanyBasics },
  { label: 'Host Types', component: StepHostTypes },
  { label: 'Confirm', component: StepConfirmation },
]

// ---------------------------------------------------------------------------
// Main Page
// ---------------------------------------------------------------------------

const initialState: OnboardingState = {
  companyName: '',
  companySlug: '',
  adminEmail: '',
  adminPassword: '',
  firstName: '',
  lastName: '',
  hostKinds: [],
  isSubmitting: false,
  isComplete: false,
}

export function OnboardingWizardPage() {
  const [step, setStep] = useState(0)
  const [state, setState] = useState<OnboardingState>(initialState)

  const update = useCallback((patch: Partial<OnboardingState>) => {
    setState((prev) => ({ ...prev, ...patch }))
  }, [])

  const next = useCallback(() => {
    setStep((s) => Math.min(s + 1, STEPS.length - 1))
  }, [])

  const back = useCallback(() => {
    setStep((s) => Math.max(s - 1, 0))
  }, [])

  const CurrentStep = useMemo(() => STEPS[step].component, [step])

  // Step heading by index
  const stepHeadings = [
    'Welcome to iWarranty',
    'What do your products attach to?',
    'Almost there',
  ]

  return (
    <div className="min-h-screen flex flex-col bg-[var(--bg)]">
      {/* Top bar with logo */}
      <div className="border-b-[0.5px] border-[var(--rule)]">
        <div className="mx-auto flex max-w-[700px] items-center justify-between px-6 py-4">
          <Logo size="md" />
          <a
            href="/login"
            className="text-[11px] text-[var(--green)] transition-colors"
          >
            Already have an account? Sign in
          </a>
        </div>
      </div>
      {/* Content */}
      <div className="flex-1 flex flex-col items-center px-6 py-10">
        <div className="w-full max-w-[540px]">
          {/* Step indicator */}
          <div className="flex items-center justify-center mb-8">
            {STEPS.map((s, i) => (
              <div key={s.label} className="flex items-center">
                <Button
                  variant="ghost"
                  onClick={() => { if (i < step && !state.isComplete) setStep(i) }}
                  className="flex items-center gap-1.5 transition-all duration-150"
                  disabled={!(i < step && !state.isComplete)}
                >
                  <span
                    className={cn(
                      'flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-full text-[9px] font-normal',
                      i <= step
                        ? 'bg-[var(--green)] text-white'
                        : 'border-[0.5px] border-[var(--rule-strong)] bg-transparent text-[var(--ink-3)]'
                    )}
                  >
                    {i < step ? '\u2713' : i + 1}
                  </span>
                  <span className={cn(
                    "whitespace-nowrap text-[10px] uppercase tracking-[0.12em]",
                    i <= step ? 'text-[var(--ink)]' : 'text-[var(--ink-3)]'
                  )}>
                    {s.label}
                  </span>
                </Button>
                {i < STEPS.length - 1 && (
                  <div
                    className={cn(
                      'mx-2 h-[0.5px] w-10 shrink-0',
                      i < step ? 'bg-[var(--green)]' : 'bg-[var(--rule-strong)]'
                    )}
                  />
                )}
              </div>
            ))}
          </div>

          {/* Step heading */}
          <div className="text-center mb-8">
            <h1 className="m-0 font-display text-[32px] leading-[1.15] text-[var(--ink)]">
              {stepHeadings[step]}
            </h1>
          </div>

          {/* Step content in a card */}
          <div
            className="rounded-[8px] border-[0.5px] border-[var(--rule)] bg-[var(--bg)] p-6"
          >
            <CurrentStep state={state} update={update} next={next} back={back} />
          </div>
        </div>
      </div>
    </div>
  );
}
